import {Component, EventEmitter, Input, Output, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormGroup, ValidatorFn, AsyncValidatorFn, Validators} from '@angular/forms';
import {FormService, LangService, LoaderService, ShortcutAction} from 'ngx-satoris';
import {FormField, VisaFormName, VisaFN, VisaFNLocal, FormAction, VisaFormConfig} from '../../shared/models/forms';
import {FormError} from 'ngx-satoris/lib/shared/models/forms';
import {CartService} from '../../shared/services/cart.service';
import {format, isAfter, startOfDay} from 'date-fns';
import {compressFile} from '../../shared/utils/compressImage';
import {ApiService} from 'src/app/shared/services/api.service';
import {PassportType} from 'src/app/shared/models/request';
import {clearInputOnlySpaces, setRequireOneOfError} from 'src/app/shared/services/form-utils';
import {dateMilliFromNowValidator} from '../../shared/utils/validators';
import {VerifyCheck} from 'src/app/shared/models/user';
import {catchError, filter, map, switchMap, take} from 'rxjs/operators';
import {of, timer} from 'rxjs';

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class FormComponent {
  @Input() formName: VisaFormName;
  @Input() classNames: string;
  @Input() showConfirm: boolean;
  @Input() config: VisaFormConfig;
  @Input() personId: number;
  @Input() submitOnStart: boolean;
  @Output() submit: EventEmitter<{form: FormGroup, byPass: boolean}> = new EventEmitter();
  @Output() actionBtn = new EventEmitter<FormAction>();

  form: FormGroup;
  submitted: boolean;
  FormAction= FormAction;
  formLoaded: boolean;
  private asyncValidatorChecks: Map<string, VerifyCheck[]> = new Map();

  constructor(public forms: FormService,
    private formBuilder: FormBuilder,
    private cart: CartService,
    private loader: LoaderService,
    private lang: LangService,
    public api: ApiService) {}

  ngOnInit() {
    this.personId = this.cart.currentRequest.person.id;
    this.config[this.formName].visited = true;
    this.form = this.formBuilder.group({});
    if(this.config[this.formName].fields) {
      this.generateForm();
    }
    if(this.submitOnStart) {
      this.submitForm();
    }
  }

  ngOnDestroy() {
    this.saveForm();
  }

  generateForm() {
    this.config[this.formName].fields.forEach((field: FormField) => {
      const originalValidators: any[] = this.cart.getOriginalForm()[this.formName].fields.find((x: { name: string; }) => x.name === field.name)?.validators;
      const fieldValidators: ValidatorFn[] = [];
      field.validators?.forEach((_validator: string | ValidatorFn) => {
        if(typeof _validator === 'string' && _validator in Validators) {
          fieldValidators.push(Validators[_validator as keyof typeof Validators] as ValidatorFn);
        }
      });

      // Add maxLength validator if field has maxLength property
      if(field.maxLength) {
        fieldValidators.push(Validators.maxLength(field.maxLength));
      }

      //add field.minimum validator if field has min property
      if(field.minimum) {
        fieldValidators.push(Validators.min(field.minimum));
      }
      //add field.maximum validator if field has max property
      if(field.maximum) {
        fieldValidators.push(Validators.max(field.maximum));
      }

      if(field.preValue) {
        field.value = field.preValue;
      }

      // Add pattern validator if field has pattern property
      if(field.pattern) {
        // Check if pattern contains Unicode properties (\\p{...})
        if(field.pattern.includes('\\p{')) {
          // Create custom validator with Unicode flag
          const unicodePatternValidator = (control: any) => {
            if(!control.value) return null;
            const regex = new RegExp(field.pattern, 'u');
            return regex.test(control.value) ? null : {pattern: {value: control.value, requiredPattern: field.pattern}};
          };
          fieldValidators.push(unicodePatternValidator);
        } else {
          // Use standard Angular validator for non-Unicode patterns
          fieldValidators.push(Validators.pattern(field.pattern));
        }
      }

      const allValidators: ValidatorFn[] = !field.onlyCustomValidators && originalValidators && originalValidators.length ? [...originalValidators, ...fieldValidators] : fieldValidators;

      let defaultValue;
      switch(field.type) {
      case 'checkbox':
        defaultValue = !!field.value || false;
        break;
      default:
        if(field.value !== undefined && field.value !== null) {
          defaultValue = field.value;
        } else if(field.defaultValue !== undefined && field.defaultValue !== null) {
          defaultValue = field.defaultValue;
        } else {
          defaultValue = '';
        }
      }

      if(field.name === VisaFN.COUNTRY_OF_BIRTH || field.name === VisaFN.MOTHER_COUNTRY_OF_BIRTH || field.name === VisaFN.FATHER_COUNTRY_OF_BIRTH) {
        const currentCountry = this.api.userInfo.server.country.toUpperCase?.() || '';
        if(Array.isArray(field.enum)) {
          const sortedEnum = [...field.enum];
          const index = sortedEnum.findIndex(opt => opt.name?.toUpperCase?.() === currentCountry || opt.value?.toUpperCase?.() === currentCountry);
          if(index > 0) {
            const [selected] = sortedEnum.splice(index, 1);
            sortedEnum.unshift(selected);
            field.enum = sortedEnum;
          }
        }
      }

      if(field.asyncValidators) {
        const doc = this.api.userInfo.server.paymentRequests.find(document => document.id === this.cart.currentRequest.visaType.id);
        this.asyncValidatorChecks.set(field.name, doc?.verifyChecks || []);
      }

      this.form.addControl(field.name, this.formBuilder.control({value: defaultValue, disabled: field.disabled}, allValidators && allValidators[0] ? allValidators : []));

      if(field.dependants) {
        field.dependants.forEach((dependant: FormField[] ) => {
          dependant.forEach((dependantField: FormField) => {
            this.form.addControl(dependantField.name, this.formBuilder.control(dependantField.value || '', [Validators.required]));
          });
        });
      }
    });

    const formFields = this.config[this.formName]?.fields || [];

    formFields.filter((field: FormField) => !!field.ifFormControlHasValue).forEach((dependentField: FormField) => {
      const {control: masterFieldNames, value: expectedValue} = dependentField.ifFormControlHasValue;
      const masterFieldNamesArray: (VisaFN | VisaFNLocal)[] = Array.isArray(masterFieldNames) ? masterFieldNames : [masterFieldNames];
      const dependentName = dependentField.name;
      const masterControl = masterFieldNamesArray.map(n => this.form.get(n)).find(c => !!c) ?? null;
      const dependentControl = this.form.get(dependentName);
      const dependentFieldConfig = formFields.find(f => f.name === dependentName);

      const updateRequired = (masterValue: any) => {
        const conditionMatched = (expectedValue !== undefined && expectedValue !== null)
          ? (Array.isArray(expectedValue) ? expectedValue.includes(masterValue) : String(masterValue) === String(expectedValue))
          : !!masterValue;
        if(dependentControl) {
          if(conditionMatched && dependentFieldConfig?.requiredIfFormControl !== false) {
            dependentControl.addValidators(Validators.required);
          } else {
            dependentControl.removeValidators(Validators.required);
          }
          dependentControl.updateValueAndValidity({emitEvent: false});
        }
        if(dependentFieldConfig) {
          const validators = Array.isArray(dependentFieldConfig.validators) ? [...dependentFieldConfig.validators] : [];
          const hasRequired = validators.includes('required');
          if(conditionMatched && !hasRequired && dependentFieldConfig?.requiredIfFormControl !== false) {
            validators.push('required');
          } else if(!conditionMatched && hasRequired) {
            dependentFieldConfig.validators = validators.filter(v => v !== 'required');
            return;
          }
          dependentFieldConfig.validators = validators;
        }
      };
      if(this.api?.env?.useGuichet) {
        if(masterControl) {
          updateRequired(masterControl.value);
          masterControl.valueChanges.subscribe(updateRequired);
        } else {
          const masterConfigValue = masterFieldNamesArray.map(n => this.getFieldFromConfig(n)?.value).find(v => !!v);
          updateRequired(masterConfigValue);
        }
      } else {
        updateRequired(null);
      }
    });

    formFields.filter(f => !!f.requiredIfFormControlHasValue).forEach(depField => {
      const {control: names, value: expected} = depField.requiredIfFormControlHasValue;
      const masterControl = (Array.isArray(names) ? names : [names]).map(n => this.form.get(n)).find(Boolean);
      const depControl = this.form.get(depField.name);
      const depConfig = formFields.find(f => f.name === depField.name);
      if(!masterControl || !depControl) return;

      const update = (val: any) => {
        const matched = expected == null ? !!val : Array.isArray(expected) ? expected.includes(val) : String(val) === String(expected);
        matched ? depControl.addValidators(Validators.required) : depControl.removeValidators(Validators.required);
        depControl.updateValueAndValidity({emitEvent: false});
        if(depConfig) depConfig.validators = matched ? [...(depConfig.validators?.filter(v => v !== 'required') ?? []), 'required'] : depConfig.validators?.filter(v => v !== 'required') ?? [];
      };
      update(masterControl.value);
      masterControl.valueChanges.subscribe(update);
    });

    this.formLoaded = true;

    if(this.config[this.formName].fields.find((field: FormField) => field.value)) {
      // Focus on first field if it has a value
      this.focusBlur();
    }

    if(this.config[this.formName].errors) {
      this.submitted = true;
    }

    setTimeout(() => {
      this.checkSameRow();
    });
  }

  submitForm(save = true, emit = true) {
    clearInputOnlySpaces(this.form, this.config[this.formName]?.fields, true);
    this.loader.loading(true);
    const ctrl = this.form.get(VisaFN.PASSPORT_NUMBER);
    if(ctrl?.value && typeof ctrl.value === 'string') {
      ctrl.setValue(ctrl.value.toUpperCase(), {emitEvent: false});
    }

    this.applyFieldTransforms();
    this.applyAsyncValidators();

    setTimeout(() => {
      this.submitted = true;
    }, 0);

    if(this.form.status !== 'PENDING') {
      this.clearAsyncValidators();
      this.checkFormValidityAndProceed(save, emit);
      this.loader.loading(false);
    } else {
      this.form.statusChanges.pipe(filter(status => status !== 'PENDING'), take(1)).subscribe(() => {
        this.clearAsyncValidators();
        this.checkFormValidityAndProceed(save, emit);
        this.loader.loading(false);
      });
    }
  }

  private checkFormValidityAndProceed(save: boolean, emit: boolean) {
    if(this.getFormErrors().length < 1) {
      this.submitted = false;
      this.config[this.formName].submitted = true;
      emit && this.submit.emit({form: this.form, byPass: this.formName === VisaFormName.DOCUMENT});
      save && this.saveForm();
    } else if(!this.form.valid && document.querySelector('.input_error')) {
      document.querySelector('.input_error').scrollIntoView({behavior: 'smooth', block: 'nearest'});
      setTimeout(() => {
        (document.querySelector('.scroll_wrapper') as any).scrollBy(0, -24, 'smooth');
        (document.querySelector('.input_error') as any)?.focus();
      }, 500);
    }
  }

  onWebcamPhoto(base64Image: string, field: FormField) {
    if(!base64Image || !field) return;

    const configField = this.config?.[this.formName]?.fields?.find((formField: FormField) => formField.name === field.name);
    if(configField) {
      configField.value = base64Image;
      configField.fileName = `${field.name}.jpg`;
    }

    const control = this.form.get(field.name);
    if(control) {
      control.setValue(base64Image);
      control.setErrors(null);
      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity({emitEvent: false});
    }
  }

  setFieldValue(field: FormField, value: any) {

    const configField = this.config?.[this.formName]?.fields?.find((formField: FormField) => formField.name === field.name);
    if(configField) {
      configField.value = value;
    }

    const control = this.form.get(field.name);
    if(control) {
      control.setValue(value);
      control.setErrors(null);
      control.markAsTouched();
      control.updateValueAndValidity({emitEvent: false});
    }
  }

  saveForm(getErrors = true) {
    this.config[this.formName].fields?.forEach((field: FormField) => {
      if(field.type !== 'file' && field.type !== 'scanDoc') {
        this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).value = this.form.get(field.name)?.value;
      }
      //if field has dependants
      if(this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).dependants) {
        this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).dependants.forEach((dependant: FormField[]) => {
          dependant.forEach((dependantField: FormField) => {
            dependantField.value = this.form.get(dependantField.name).value;
            dependantField.validators = ['required'];
          });
        });
      }
    });
    if(getErrors) this.config[this.formName].errors = this.getFormErrors();
    if(this.cart.currentApplication?.requests.length > 0) {
      const i = this.cart.currentApplication.requests.findIndex(request => request.person.id === this.personId);
      this.cart.currentApplication.requests[i].formConfig = this.config;
      this.cart.saveCurrentApplication(false);
    }
  }

  checkIfFormControl(field: FormField, _index?: number) {
    if(field.alwaysVisible && !field.ifFormControlHasValue) return true;
    if(field.ifFormControl) {
      return this.form.controls[field.ifFormControl]?.value === true || this.form.controls[field.ifFormControl]?.value === 'true';
    } else if(field.ifFormControlHasNotValue) {
      return this.form.controls[field.ifFormControlHasNotValue.control]?.value && this.form.controls[field.ifFormControlHasNotValue.control]?.value !== field.ifFormControlHasNotValue.value;
    } else if(field.ifFormControlHasValue) {
      const controls = Array.isArray(field.ifFormControlHasValue.control) ? field.ifFormControlHasValue.control : [field.ifFormControlHasValue.control];
      const hasControlValue = controls.some((controlName: VisaFN) => {
        const masterValue = this.form.get(controlName)?.value || this.getFieldFromConfig(controlName)?.value;
        if(field.ifFormControlHasValue.value !== undefined) {
          return Array.isArray(field.ifFormControlHasValue.value)
            ? field.ifFormControlHasValue.value.includes(masterValue)
            : masterValue === field.ifFormControlHasValue.value;
        }
        return !!masterValue;
      });
      return hasControlValue;
    } else if(field.ifFormControlHasMinDateMilliFromNow) {
      const controlRawValue = this.form.get(field.ifFormControlHasMinDateMilliFromNow.control)?.value ?? this.config[field.ifFormControlHasMinDateMilliFromNow.form].fields.find((formField: FormField) => formField.name === field.ifFormControlHasMinDateMilliFromNow.control)?.value;
      if(controlRawValue) {
        const targetDate = new Date(controlRawValue);
        return targetDate.getTime() >= new Date(field.ifFormControlHasMinDateMilliFromNow.value).getTime();
      }
      return false;
    } else {
      return true;
    }
  }

  addDependant(field: FormField, index: number) {
    if(!field.dependants) return;
    //push new dependant
    const dependant: FormField[] = [
      {name: 'dependants_name_'+(index+1), type: 'text', ifFormControl: VisaFN.DEPENDANTS, validators: []},
      {name: 'dependants_passport_number_'+(index+1), type: 'text', ifFormControl: VisaFN.DEPENDANTS, validators: []},
      {name: 'dependants_date_of_birth_'+(index+1), type: 'date', ifFormControl: VisaFN.DEPENDANTS, validators: []},
      {name: 'dependants_place_of_birth_'+(index+1), type: 'text', ifFormControl: VisaFN.DEPENDANTS, validators: []}
    ];
    dependant.forEach((dependantField: FormField) => {
      this.form.addControl(dependantField.name, this.formBuilder.control('', [Validators.required]));
    });
    field.dependants.push(dependant);
  }

  removeDependant(field: FormField, index: number) {
    if(!field.dependants) return;
    if(index === 0 && field.dependants.length === 1) {
      this.form.controls[field.name]?.setValue(false);
      this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).value = false;
    } else {
      this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).dependants[index].forEach((dependantField: FormField) => {
        this.form.removeControl(dependantField.name);
      } );
      this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).dependants.splice(index, 1);
    }
  }

  getFormErrors(): FormError[] {
    clearInputOnlySpaces(this.form, this.config[this.formName]?.fields, false);
    if(this.formName === VisaFormName.DEPENDANTS) {
      const errors: FormError[] = this.forms.getFormErrors(this.form);
      errors.forEach((error: FormError) => {
        error.key = error.key.replace(/_\d/g, '');
      });
      return errors;
    } else if(this.formName === VisaFormName.DOCUMENT) {
      //check if value is not empty in saved config
      const errorsFC: string[] = [];
      const processedRequireOneOfGroups = new Set<string>();
      this.config[this.formName].fields.forEach((field: FormField) => {
        if(!field.value && field.validators?.includes('required')) {
          errorsFC.push(field.name);
        }
        if(field.requireOneOf && !field.hidden) {
          const group = field.requireOneOf;
          const groupKey = group.sort().join('|');
          if(!processedRequireOneOfGroups.has(groupKey)) {
            processedRequireOneOfGroups.add(groupKey);
            const hasError = setRequireOneOfError(this.form, group);
            if(hasError) {
              errorsFC.push(group[0]);
            }
          }
        }
      });
      const errs: FormError[] = this.forms.getFormErrors(this.form);
      errorsFC.forEach((key: string) => {
        if(!errs.find((e: FormError) => e.key === key)) {
          errs.push({key, keyError: 'required'});
        }
      });
      return errs;
    } else {
      //check if field type date and if errors from date validators
      this.config[this.formName].fields.forEach((field: FormField) => {
        let errors = 0;


        // Handle conditional enable/disable based on min date BEFORE other validations
        if(field.ifFormControlHasMinDateMilliFromNow) {
          const controlRawValue = this.form.get(field.ifFormControlHasMinDateMilliFromNow.control)?.value ?? this.config[field.ifFormControlHasMinDateMilliFromNow.form].fields.find((formField: FormField) => formField.name === field.ifFormControlHasMinDateMilliFromNow.control)?.value;
          const shouldEnable = !!controlRawValue && new Date(controlRawValue).getTime() >= new Date(field.ifFormControlHasMinDateMilliFromNow.value).getTime();

          if(shouldEnable) {
            this.form.get(field.name).enable();
          } else {
            this.form.get(field.name).setValue(null);
            this.form.get(field.name).disable();
          }
        }
        if(field.type === 'date') {
          if(field.dateValidators) {
            if(field.dateValidators?.maxDateMilliFromToday !== undefined) {
              const inputDate = startOfDay(new Date(this.form.get(field.name).value));
              const now = startOfDay(new Date());
              const maxAllowed = new Date(now.getTime() + field.dateValidators.maxDateMilliFromToday);
              if(isAfter(inputDate, maxAllowed)) {
                errors++;
                this.form.get(field.name).setErrors({...this.form.get(field.name).errors, maxDateMilliFromToday: {maxAllowed: format(maxAllowed, 'MM-dd-yyyy')}});
              }
            }
            if(field.dateValidators?.mustBeEqualOrBefore) {
              const targetFieldName = typeof field.dateValidators.mustBeEqualOrBefore === 'string' ? field.dateValidators.mustBeEqualOrBefore : field.dateValidators.mustBeEqualOrBefore.control;
              const inputDate = startOfDay(new Date(this.form.get(field.name).value));
              const target = this.form.get(targetFieldName)?.value || this.getFieldFromConfig(targetFieldName)?.value;
              const targetDate = startOfDay(new Date(target));
              if(isAfter(inputDate, targetDate)) {
                errors++;
                this.form.get(field.name).setErrors({...this.form.get(field.name).errors, mustBeEqualOrBefore: {target: this.lang.transform(targetFieldName)}});
              }
            }
            if(field.dateValidators?.mustBeEqualOrAfter) {
              const targetFieldName = typeof field.dateValidators.mustBeEqualOrAfter === 'string' ? field.dateValidators.mustBeEqualOrAfter : field.dateValidators.mustBeEqualOrAfter.control;
              const inputDate = startOfDay(new Date(this.form.get(field.name).value));
              const target = this.form.get(targetFieldName)?.value || this.getFieldFromConfig(targetFieldName)?.value;
              const targetDate = startOfDay(new Date(target));
              if(isAfter(targetDate, inputDate)) {
                errors++;
                this.form.get(field.name).setErrors({...this.form.get(field.name).errors, mustBeEqualOrAfter: {target: this.lang.transform(targetFieldName)}
                });
              }
            }
            if(field.dateValidators?.maxDateMilliTo !== undefined) {
              const {control, milliseconds} = field.dateValidators.maxDateMilliTo;
              const targetValue = this.form.get(control)?.value ?? this.getFieldFromConfig(control)?.value;
              const inputTime = startOfDay(new Date(this.form.get(field.name).value)).getTime();
              const targetTime = startOfDay(new Date(targetValue)).getTime();
              const diff = Math.abs(inputTime - targetTime);
              if(diff < milliseconds) {
                const limitTime = inputTime < targetTime ? targetTime - milliseconds : targetTime + milliseconds;
                errors++;
                this.form.get(field.name).setErrors({...this.form.get(field.name).errors, maxDateMilliTo: {limitDate: format(limitTime, 'MM-dd-yyyy'), control: this.lang.transform(control)}});
              }
            }
            if(field.dateValidators?.maxDateMilliFrom !== undefined) {
              const {control, milliseconds} = field.dateValidators.maxDateMilliFrom;
              const inputValue = this.form.get(field.name)?.value;
              const targetValue = this.form.get(control)?.value ?? this.getFieldFromConfig(control)?.value;
              if(!inputValue || !targetValue) return;
              const inputDate = startOfDay(new Date(inputValue));
              const targetDate = startOfDay(new Date(targetValue));
              const limitDate = startOfDay(new Date(targetDate.getTime() + milliseconds));
              if(inputDate.getTime() > limitDate.getTime()) {
                errors++;
                this.form.get(field.name).setErrors({...this.form.get(field.name).errors, maxDateMilliFrom: {limiteDate: format(limitDate, 'MM-dd-yyyy'), control: this.lang.transform?.(control)}});
              }
            }
            if(field.dateValidators?.maxDateMilliFromNow !== undefined || field.dateValidators?.minDateMilliFromNow !== undefined) {
              const ctrl = this.form.get(field.name);
              if(ctrl) {
                const result = dateMilliFromNowValidator({
                  maxDateMilliFromNow: field.dateValidators.maxDateMilliFromNow,
                  minDateMilliFromNow: field.dateValidators.minDateMilliFromNow
                })(ctrl);
                if(result) {
                  errors += Object.keys(result).length;
                  ctrl.setErrors({...ctrl.errors, ...result});
                }
              }
            }
          }
        }
        if(field.mustBeDifferentThan) {
          const rawValue = this.form.get(field.name).value;
          const normalizedValue = typeof rawValue === 'object' && rawValue !== null ? (rawValue.value ?? rawValue.name ?? '') : rawValue;


          if(field.mustBeDifferentThan.array) {
            const array = field.mustBeDifferentThan.array;
            const isEmptyValue = normalizedValue === undefined || normalizedValue === null || String(normalizedValue).trim() === '';
            const includesEmpty = array.some((item: any) => {
              const normalizedItem = typeof item === 'object' && item !== null ? (item.value ?? item.name ?? '') : item;
              return normalizedItem === undefined || normalizedItem === null || String(normalizedItem).trim() === '';
            });

            // If empty is forbidden and current value is empty, set required immediately
            if(includesEmpty && isEmptyValue) {
              errors++;
              this.form.get(field.name).setErrors({...this.form.get(field.name).errors, required: true});
            } else {
              // Otherwise, check membership ignoring empty entries
              const isInArray = array.some((item: any) => {
                const normalizedItem = typeof item === 'object' && item !== null ? (item.value ?? item.name ?? '') : item;
                if(normalizedItem === undefined || normalizedItem === null || String(normalizedItem).trim() === '') return false;
                return String(normalizedItem).toLowerCase() === String(normalizedValue).toLowerCase();
              });
              if(isInArray) {
                errors++;
                this.form.get(field.name).setErrors({...this.form.get(field.name).errors, mustBeDifferentThan: {mustBeDifferentThan: field.mustBeDifferentThan.array, control: this.lang.transform(field.name)}});
              }
            }
          }
          if(field.mustBeDifferentThan.value !== undefined && field.mustBeDifferentThan.value !== null) {
            if(String(normalizedValue).toLowerCase() === String(field.mustBeDifferentThan.value).toLowerCase()) {
              errors++;
              this.form.get(field.name).setErrors({...this.form.get(field.name).errors, mustBeDifferentThan: {mustBeDifferentThan: field.mustBeDifferentThan.value, control: this.lang.transform(field.name)}});
            }
          }
        }
        if(field.maxLength) {
          if(this.form.get(field.name).value?.length > field.maxLength) {
            errors++;
            this.form.get(field.name).setErrors({...this.form.get(field.name).errors, maxlength: true});
          }
        }
        if(field.ifFormControl) {
          if(this.form.controls[field.ifFormControl]?.value === true || this.form.controls[field.ifFormControl]?.value === 'true') {
            if(this.form.get(field.name).disabled) {
              this.form.get(field.name).enable({emitEvent: false});
            }
            if(field?.requiredIfFormControl && !this.form.get(field.name).value) {
              errors++;
              this.form.get(field.name).setErrors({...this.form.get(field.name).errors, required: true});
            }
          } else {
            this.clearFieldValue(field);
            this.form.get(field.name).disable({emitEvent: false});
            this.form.get(field.name).setErrors(null);
            this.form.get(field.name).updateValueAndValidity({emitEvent: false});
          }
        }
        if(field.ifFormControlHasNotValue) {
          const controlValue = this.form.controls[field.ifFormControlHasNotValue.control]?.value ?? this.getFieldFromConfig(field.ifFormControlHasNotValue.control)?.value;
          if(!field.hidden && controlValue && controlValue !== field.ifFormControlHasNotValue.value) {
            if(this.form.get(field.name).disabled) {
              this.form.get(field.name).enable({emitEvent: false});
            }
            if(field?.requiredIfFormControl && !this.form.get(field.name).value) {
              errors++;
              this.form.get(field.name).setErrors({...this.form.get(field.name).errors, required: true});
            }
          } else {
            this.clearFieldValue(field);
            this.form.get(field.name).disable({emitEvent: false});
            this.form.get(field.name).setErrors(null);
            this.form.get(field.name).updateValueAndValidity({emitEvent: false});
          }
        }
        if(field.ifFormControlHasValue) {
          const controls = Array.isArray(field.ifFormControlHasValue.control) ? field.ifFormControlHasValue.control : [field.ifFormControlHasValue.control];
          const hasControlValue = controls.some((controlName: VisaFN) => {
            const controlValue = this.form.controls[controlName]?.value ?? this.getFieldFromConfig(controlName)?.value;
            if(field.ifFormControlHasValue.value !== undefined) {
              return Array.isArray(field.ifFormControlHasValue.value)
                ? field.ifFormControlHasValue.value.includes(controlValue)
                : controlValue === field.ifFormControlHasValue.value;
            }
            return !!controlValue;
          });
          if(hasControlValue) {
            if(this.form.get(field.name).disabled) {
              this.form.get(field.name).enable({emitEvent: false});
            }
            if(field?.requiredIfFormControl && !this.form.get(field.name).value) {
              errors++;
              this.form.get(field.name).setErrors({...this.form.get(field.name).errors, required: true});
            }
          } else {
            this.clearFieldValue(field);
            this.form.get(field.name).disable({emitEvent: false});
            this.form.get(field.name).setErrors(null);
            this.form.get(field.name).updateValueAndValidity({emitEvent: false});
          }
        }

        if(this.cart.currentApplication.isRevision && this.config[this.formName].errors.find((error: FormError) => error.key === field.name)) {
          if(!this.form.controls[field.name].value) {
            errors++;
            //this.config[this.formName].errors = this.config[this.formName].errors.filter((error: FormError) => error.key !== field.name);
            this.form.get(field.name).setErrors({...this.form.get(field.name).errors, required: true});
            this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).validators = ['required'];
          }
        }
        if(field.minimum) {
          if(this.form.get(field.name).status !== 'DISABLED' && this.form.get(field.name).value < field.minimum) {
            errors++;
            this.form.get(field.name).setErrors({...this.form.get(field.name).errors, min: true});
          }
        }
        if(field.maximum) {
          if(this.form.get(field.name).status !== 'DISABLED' && this.form.get(field.name).value > field.maximum) {
            errors++;
            this.form.get(field.name).setErrors({...this.form.get(field.name).errors, max: true});
          }
        }
        if(field.requireOneOf) {
          const group = field.requireOneOf;
          const baseKey = group[0];
          // Only handle once per group on the baseKey: call helper on the base control
          if(field.name === baseKey) {
            const added = setRequireOneOfError(this.form, group, baseKey, 'requireOneOf');
            if(added) {
              errors++;
              const baseCtrl = this.form.get(baseKey);
              if(baseCtrl?.errors?.requireOneOf) {
                const formattedFields = this.formatRequireOneOfFieldNames(group);
                baseCtrl.setErrors({...baseCtrl.errors, requireOneOf: {errorValue: formattedFields}});
              }
            }
          }
        }
        // if no errors, or control is actually disabled, clear errors
        if(errors === 0 || field.disabled) {
          const ctrl = this.form.get(field.name);

          if(ctrl && (errors === 0 || field.disabled) && ctrl.status !== 'PENDING' && ctrl.status !== 'INVALID') {
            ctrl.setErrors(null);
            ctrl.updateValueAndValidity();
          }
        }
      });
      this.form.updateValueAndValidity();
      return this.forms.getFormErrors(this.form);
    }
  }

  private focusBlur() {
    setTimeout(() => {
      document.querySelector('input')?.focus();
      document.querySelector('input')?.blur();
    });
  }

  removeUnderscoreNumber(name: string) {
    return name.replace(/_\d/g, '');
  }

  outputInput($event: any, field: FormField) {
    if($event[0] instanceof File || $event?.files?.[0] instanceof File) {
      const file: File = $event[0] || $event.files[0];
      // If the file is a PDF and size is less than or equal to 1MB
      if(file.type === 'application/pdf') {
        if((file.size / 1024 / 1024) <= 1) {
          const reader = new FileReader();
          reader.onload = (event: any) => {
            this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).fileName = file.name;
            this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).value = event.target.result;
            const ctrl = this.form.get(field.name);
            if(ctrl) {
              ctrl.setErrors(null);
              ctrl.markAsTouched();
              ctrl.markAsDirty();
            }
          };
          reader.readAsDataURL(file);
        } else {
          this.loader.loading(true, {type: 'error', message: this.lang.transform('err.file.size') + ' 1MB'});
        }
      } else {
        this.loader.loading(true);
        compressFile(file, 0.5).then((b64: string) => {
          this.loader.loading(false);
          this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).fileName = file.name;
          this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).value = b64;
          const ctrl = this.form.get(field.name);
          if(ctrl) {
            ctrl.setErrors(null);
            ctrl.markAsTouched();
            ctrl.markAsDirty();
          }
        }).catch((err: any) => {
          this.loader.loading(true, {type: 'error', message: err});
        });
      }
    }
  }

  fieldRequired(field: FormField) {
    return this.form.get(field.name)?.errors?.required;
  }

  resetFileField(field: FormField) {
    this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).value = '';
    this.config[this.formName].fields.find((formField: FormField) => formField.name === field.name).fileName = '';
  }

  visualFile(event: any) {
    this.config[this.formName].fields;
    this.loader.loading(true, {
      hideHeader: true,
      fullPage: false,
      custom: {
        innerHtml: `<div class="custom-popup">
          <img src="${event}" alt="popup image" />
        </div>`
      }
    });
  }

  doAction(action: FormAction, event?: any) {
    if(action === FormAction.SUBMIT) {
      this.submitForm(false, false);
      this.saveForm();
    } else if(action === FormAction.FOCUS_BLUR) {
      this.focusBlur();
    } else if(action === FormAction.SAVE) {
      this.saveForm(false);
    } else if(action === FormAction.UPLOAD_FILE) {
      if(event?.error?.message === 'err.fileSize') {
        this.loader.loading(true, {type: 'error', message: this.lang.transform('err.file.size') + ' 1MB'});
      }
    } else if(action === FormAction.SELECT_PASSPORT_TYPE) {
      const type = event as PassportType;
      if(type !== PassportType.ORDINARY) {
        this.loader.loading(true, {type: 'info', message: 'passportType.acknowledge', shortcut: {closeBtn: {action: ShortcutAction.ENTER}}});
      }
    }
  }

  checkSameRow() {
    // Group fields by their sameRow values
    const sameRowGroups = new Map<string, string[]>();
    this.config[this.formName].fields.filter((formField: FormField) => formField.sameRow).forEach((formField: FormField) => {
      const sameRowValue = formField.sameRow;
      if(!sameRowGroups.has(sameRowValue)) {
        sameRowGroups.set(sameRowValue, []);
      }
      sameRowGroups.get(sameRowValue).push(formField.name);
    });

    sameRowGroups.forEach((sameRowFieldsNames: string[], sameRowValue: string) => {
      const containerId = `sameRowContainer_${sameRowValue}`;
      if(document.getElementById(containerId)) {
        return;
      }
      if(sameRowFieldsNames.length < 2) return;
      const div1 = document.createElement('div');
      const div2 = document.createElement('div');
      div2.id = containerId;
      div1.classList.add('f_col', 'fullWidth');

      const elements: any[] = [];
      document.getElementById('wrap_'+sameRowFieldsNames[0])?.parentNode.insertBefore(div1, document.getElementById('wrap_'+sameRowFieldsNames[0]));
      sameRowFieldsNames.forEach((name: string) => {
        if(document.getElementById('wrap_'+name)) {
          elements.push(document.getElementById('wrap_'+name));
          document.getElementById('wrap_'+name).remove();
        }
      });
      elements.forEach((element: any) => {
        div2.appendChild(element);
      });
      const p = document.createElement('div');
      const descKey = sameRowValue + '_desc';
      const descriptionText = this.lang.transform(descKey);
      if(descriptionText && descriptionText.trim() && descriptionText !== descKey) {
        p.innerHTML = descriptionText;
        div1.appendChild(p);
        // Only add margin-top when there's a description above
        div2.classList.add('flex', 'gap_10', 'fullWidth', 'mt_24');
      } else {
        // No margin-top when there's no description
        div2.classList.add('flex', 'gap_10', 'fullWidth');
      }
      div1.appendChild(div2);
    });
  }

  getFileSize(fileSize: number): string {
    return fileSize/1000 >= 1000 ? (fileSize / 1000 / 1000) + 'Mb' : fileSize/1000 + 'Kb';
  }

  getFieldFromConfig(fieldName: VisaFN | VisaFNLocal): FormField | undefined {
    for(const stepName of Object.keys(this.config)) {
      const field = this.config[stepName]?.fields.find((f: FormField) => f.name === fieldName);
      if(field) return field;
    }
    return undefined;
  }

  private clearFieldValue(field: FormField) {
    const control = this.form.get(field.name);
    if(control) {
      const emptyValue = field.type === 'checkbox' ? false : '';
      control.setValue(emptyValue, {emitEvent: false});
    }
    const cfgField = this.config[this.formName]?.fields?.find((f: FormField) => f.name === field.name);
    if(cfgField) {
      (cfgField as any).value = field.type === 'checkbox' ? false : '';
      if(field.type === 'file') (cfgField as any).fileName = '';
    }
  }

  getMinDate(field: FormField): Date | null {
    if(!field.dateValidators) return null;

    if(field.dateValidators.minDate) {
      return new Date(field.dateValidators.minDate);
    }

    if(field.dateValidators.mustBeEqualOrAfter) {
      const controlValue = this.form.get(field.dateValidators.mustBeEqualOrBefore.control)?.value ? startOfDay(new Date(this.form.get(field.dateValidators.mustBeEqualOrBefore.control)?.value)) : startOfDay(new Date(this.config[field.dateValidators.mustBeEqualOrBefore.form].fields.find((formField: FormField) => formField.name === field.dateValidators.mustBeEqualOrBefore.control).value));
      if(controlValue) {
        return new Date(controlValue);
      }
    }

    return null;
  }

  getMaxDate(field: FormField): Date | null {
    if(!field.dateValidators) return null;

    if(field.dateValidators.maxDate) {
      return new Date(field.dateValidators.maxDate);
    }
    if(field.dateValidators.maxDateMilliFromToday) {
      return new Date(Date.now() + field.dateValidators.maxDateMilliFromToday);
    }

    if(field.dateValidators.mustBeEqualOrBefore) {
      const controlValue =  this.form.get(field.dateValidators.mustBeEqualOrAfter.control)?.value ? startOfDay(new Date(this.form.get(field.dateValidators.mustBeEqualOrAfter.control)?.value)) : startOfDay(new Date(this.config[field.dateValidators.mustBeEqualOrAfter.form].fields.find((formField: FormField) => formField.name === field.dateValidators.mustBeEqualOrAfter.control).value));
      if(controlValue) {
        return new Date(controlValue);
      }
    }
    return null;
  }

  private clearAsyncValidators(): void {
    this.asyncValidatorChecks.forEach((_: VerifyCheck[], fieldName: string) => {
      const control = this.form.get(fieldName);
      if(control) {
        control.clearAsyncValidators();
      }
    });
  }

  private applyFieldTransforms(): void {
    this.cart.getOriginalForm()[this.formName]?.fields?.forEach((field: FormField) => {
      if(!field.transform) return;
      const control = this.form.get(field.name);
      if(!control || typeof control.value !== 'string') return;
      const transformed = field.transform(control.value);
      if(transformed !== control.value) {
        control.setValue(transformed, {emitEvent: false});
      }
    });
  }

  private applyAsyncValidators(): void {
    this.asyncValidatorChecks.forEach((verifyChecks: VerifyCheck[], fieldName: string) => {
      const control = this.form.get(fieldName);
      if(control && verifyChecks.length > 0) {
        const asyncValidators = this.createAsyncValidators(verifyChecks, fieldName);
        if(asyncValidators.length > 0) {
          control.setAsyncValidators(asyncValidators);
          control.updateValueAndValidity({emitEvent: false});
        }
      }
    });
  }

  private createAsyncValidators(verifyChecks: VerifyCheck[], fieldName: string): AsyncValidatorFn[] {
    const asyncValidators: AsyncValidatorFn[] = [];
    verifyChecks?.forEach((check: VerifyCheck) => {
      const validator = this.getAsyncValidator(check, fieldName);
      if(validator) {
        asyncValidators.push(validator);
      }
    });
    return asyncValidators;
  }

  
  private formatRequireOneOfFieldNames(fieldNames: string[]): string {
    const fieldLabel = this.lang.transform('err.requireOneOf.field');
    const translatedNames = fieldNames.map(name => fieldLabel.replace('{name}', this.lang.transform(name)));
    if(translatedNames.length === 1) {
      return translatedNames[0];
    }
    const separator = this.lang.transform('err.requireOneOf.separator');
    return translatedNames.slice(0, -1).join(separator) + separator + translatedNames[translatedNames.length - 1];
  }

  private getAsyncValidator(check: VerifyCheck, fieldName: string): AsyncValidatorFn | null {
    return (control) => {
      if(!control.value || control.disabled) {
        return of(null);
      }
      return timer(200).pipe(switchMap(() => this.api.verifyCheck(
        check.name,
        this.cart.currentRequest.visaType.id,
        this.buildVerifyCheckPayload(check, fieldName, control.value)
      )),
      map(() => null),
      catchError(() => of({[check.name]: true})));
    };
  }

  private buildVerifyCheckPayload(check: VerifyCheck, fallbackFieldName: string, fallbackValue: any): Record<string, any> {
    const payload: Record<string, any> = {};
    const keys = check.keys?.length ? check.keys : [fallbackFieldName];
    keys.forEach((key: string) => {
      if(key === fallbackFieldName) {
        payload[key] = this.normalizeVerifyCheckValue(fallbackValue);
        return;
      }
      const controlValue = this.form.get(key as VisaFN)?.value;
      const configValue = this.getFieldFromConfig(key as VisaFN)?.value;
      payload[key] = this.normalizeVerifyCheckValue(controlValue ?? configValue);
    });
    return payload;
  }

  private normalizeVerifyCheckValue(value: any): any {
    if(value === undefined || value === null) return undefined;
    return typeof value === 'string' ? value.toString().trim() : value;
  }
}

